import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Copy } from 'lucide-react';
import EditEvent from './EditEvent';
import DuplicateEventModal from './DuplicateEventModal';
import { useAppState } from '../contexts/AppStateContext';
import api from '../utils/axiosConfig';

const DisplayEvent = () => {
  const { currentEvent, currentOrganization, hasRole, addNewEvent } = useAppState();
  const [event, setEvent] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDuplicateOpen, setIsDuplicateOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch event details
  useEffect(() => {
    const fetchEventDetails = async () => {
      if (!currentEvent || !currentEvent.id) return;

      setLoading(true);
      setError(null);

      try {
        const response = await api.get(`/organizations/${currentOrganization.id}/events/${currentEvent.id}`);
        setEvent(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching event details:', error);
        setError('Failed to load event details. Please try again.');
        setLoading(false);
      }
    };

    fetchEventDetails();
  }, [currentEvent]);

  const handleUpdate = async (updatedData) => {
    // If no changes and user clicked cancel, just exit edit mode
    if (updatedData === event) {
      setIsEditMode(false);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Check if updatedData is a FormData object (which means it contains files)
      const isFormData = updatedData instanceof FormData;

      // Check if FormData has any entries
      if (isFormData && [...updatedData.entries()].length === 0) {
        console.log('No changes to submit');
        setIsEditMode(false);
        setSubmitting(false);
        return;
      }

      let response;
      if (isFormData) {
        // Make sure the FormData request has the right config for multipart/form-data
        response = await api.put(
          `/organizations/${currentOrganization.id}/events/${currentEvent.id}/`,
          updatedData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      } else {
        // Regular JSON data - use PATCH instead of PUT to update only changed fields
        response = await api.patch(
          `/organizations/${currentOrganization.id}/events/${currentEvent.id}/`,
          updatedData
        );
      }

      // Update the event state with the response data
      setEvent(response.data);
      setIsEditMode(false);
    } catch (error) {
      console.error('Error updating event:', error);
      setError('Failed to update event. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDuplicated = async (newEvent) => {
    setIsDuplicateOpen(false);
    // Refresh the event list and switch to the new occurrence
    await addNewEvent(newEvent.id);
  };

  // Duplicating creates a new event, so it follows the same rule as "Create Event"
  const canDuplicate = hasRole(['owner', 'admin']);

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      return format(new Date(dateString), 'PPP p'); // Format: "Apr 29, 2021 12:00 PM"
    } catch {
      return dateString;
    }
  };

  // Modified to group related fields together
  const formGroups = [
    {
      title: "General Info",
      fields: [
        ["name"], // Single field
        ["custom_domain"], // Single field
        ["date"], // Single field
        ["logo", "ticket_image"], // Logo and ticket image paired
        ["landing_page", "embedded_form_link"], // Landing page and embedded form link paired
      ],
      roles: ['owner', 'admin', 'event_manager', 'usher'],
    },
    {
      title: "Payment and Registration",
      fields: [
        ["auto_approve"],
        ["is_paid"],
        ["form_structure"],
        ["registration_start_date", "registration_end_date"], // Registration dates paired
        ["allow_multiple_tickets_purchase"],
        ["max_tickets_per_customer"],
      ],
      roles: ['owner', 'admin', 'event_manager'],
    },
    {
      title: "Communication",
      fields: [
        ["submission_approved_email"],
        ["submission_rejected_email"],
        ["submission_received_email"],
        ["event_reminder_email"],
      ],
      roles: ['owner', 'admin', 'event_manager'],
    }
  ];

  // Render individual field
  const renderSingleField = (fieldName) => {
    if (!event) return null;

    switch (fieldName) {
      case 'name':
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">Event Name</label>
            <div className="mt-1 p-2 border border-gray-300 rounded-md bg-gray-50">{event.name || 'Not set'}</div>
          </div>
        );

      case 'custom_domain':
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">Custom Domain</label>
            <div className="mt-1 p-2 border border-gray-300 rounded-md bg-gray-50">{event.custom_domain?.domain || 'Not set'}</div>
          </div>
        );

      case 'date':
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">Event Date</label>
            <div className="mt-1 p-2 border border-gray-300 rounded-md bg-gray-50">{formatDate(event.date)}</div>
          </div>
        );

      case 'logo':
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">Event Logo</label>
            {event.logo ? (
              <div className="mt-2">
                <img
                  src={event.logo}
                  alt={`${event.name} logo`}
                  className="h-20 w-auto object-contain border border-gray-300 rounded-md p-1"
                />
              </div>
            ) : (
              <div className="mt-1 p-2 border border-gray-300 rounded-md bg-gray-50">No logo</div>
            )}
          </div>
        );

      case 'ticket_image':
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">Ticket Image</label>
            {event.ticket_image ? (
              <div className="mt-2">
                <img
                  src={event.ticket_image}
                  alt="Ticket Image"
                  className="h-20 w-auto object-contain border border-gray-300 rounded-md p-1"
                />
              </div>
            ) : (
              <div className="mt-1 p-2 border border-gray-300 rounded-md bg-gray-50">No ticket design</div>
            )}
          </div>
        );
      case 'auto_approve':
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">Auto Approve Registrations</label>
            <div className="mt-1 p-2 border border-gray-300 rounded-md bg-gray-50">
              {event.auto_approve ? 'Yes' : 'No'}
            </div>
          </div>
        );

      case 'is_paid':
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">Paid Event</label>
            <div className="mt-1 p-2 border border-gray-300 rounded-md bg-gray-50">
              {event.is_paid ? 'Yes' : 'No'}
            </div>
          </div>
        );

      case 'registration_start_date':
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">Registration Start Date</label>
            <div className="mt-1 p-2 border border-gray-300 rounded-md bg-gray-50">
              {formatDate(event.registration_start_date)}
            </div>
          </div>
        );

      case 'registration_end_date':
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">Registration End Date</label>
            <div className="mt-1 p-2 border border-gray-300 rounded-md bg-gray-50">
              {formatDate(event.registration_end_date)}
            </div>
          </div>
        );

      case 'allow_multiple_tickets_purchase':
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">Allow Multiple Tickets Purchase</label>
            <div className="mt-1 p-2 border border-gray-300 rounded-md bg-gray-50">
              {event.allow_multiple_tickets_purchase ? 'Allowed' : 'Not Allowed'}
            </div>
          </div>
        );
      case 'max_tickets_per_customer':
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">Max Tickets Per Customer</label>
            <div className="mt-1 p-2 border border-gray-300 rounded-md bg-gray-50">
              {event.max_tickets_per_customer ? event.max_tickets_per_customer : 'Unlimited'}
            </div>
          </div>
        );
      case 'landing_page':
      case 'embedded_form_link':
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">{fieldName.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}</label>
            <div className="mt-1 p-2 border border-gray-300 rounded-md bg-gray-50">
              <a href={event[fieldName]} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                {event[fieldName]}
              </a>
            </div>
          </div>
        );
      case 'submission_approved_email':
      case 'submission_rejected_email':
      case 'submission_received_email':
      case 'event_reminder_email':
        const emailLabels = {
          'submission_approved_email': 'Submission Approved Email Template',
          'submission_rejected_email': 'Submission Rejected Email Template',
          'submission_received_email': 'Submission Received Email Template',
          'event_reminder_email': 'Event Reminder Email Template'
        };

        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">{emailLabels[fieldName]}</label>
            <div className="mt-1 p-2 border border-gray-300 rounded-md bg-gray-50 max-h-40 overflow-auto">
              {event[fieldName] ? event[fieldName] : 'Not configured'}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Render a row of fields (either one or two fields)
  const renderFieldRow = (fieldRow) => {
    if (fieldRow.length === 1) {
      // Single field occupies full width
      return (
        <div className="col-span-2">
          {renderSingleField(fieldRow[0])}
        </div>
      );
    } else if (fieldRow.length === 2) {
      // Two fields side by side
      return (
        <>
          <div className="col-span-1">
            {renderSingleField(fieldRow[0])}
          </div>
          <div className="col-span-1">
            {renderSingleField(fieldRow[1])}
          </div>
        </>
      );
    }
    return null;
  };

  if (!currentEvent) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Please select an event to view details</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="flex justify-center">
          <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <p className="text-gray-500 mt-2">Loading event details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-500">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (submitting) {
    return (
      <div className="p-6 text-center">
        <div className="flex justify-center">
          <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <p className="text-gray-500 mt-2">Updating event...</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6">
      {!event ? (
        <p className="text-center">Loading...</p>
      ) : (
        <>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{event.name}</h2>

            {/* Edit mode toggle */}
            {hasRole(['admin', 'event_manager', 'owner']) && (
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-gray-700">Edit Mode</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isEditMode}
                    onChange={() => setIsEditMode(!isEditMode)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>
            )}
          </div>

          {canDuplicate && (
            <DuplicateEventModal
              isOpen={isDuplicateOpen}
              onClose={() => setIsDuplicateOpen(false)}
              event={event}
              onDuplicated={handleDuplicated}
            />
          )}

          {isEditMode ? (
            <EditEvent event={event} onUpdate={handleUpdate} />
          ) : (
            <div className="space-y-8">
              {formGroups.map((group, groupIndex) => (
                <div key={groupIndex} className="mb-8">
                  {hasRole(group.roles) && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b">{group.title}</h3>
                      <div className="grid grid-cols-2 gap-6">
                        {group.fields.map((fieldRow, rowIndex) => (
                          <React.Fragment key={rowIndex}>
                            {renderFieldRow(fieldRow)}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {canDuplicate && (
                <div className="pt-6 border-t flex justify-end">
                  <button
                    type="button"
                    onClick={() => setIsDuplicateOpen(true)}
                    className="flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <Copy size={16} className="mr-2" />
                    Duplicate Event
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DisplayEvent;